import { useQuery, UseQueryOptions } from '@tanstack/react-query'
import { APIList } from 'src/interfaces'
import { Entities } from 'src/enums'
import { Contractor } from 'src/api'
import { useApiInstance } from './useApiInstance'
import {
  buildRequestParams,
  ObjectType,
  transformPage,
  transformPerPage,
} from './helpers'

interface RequestParams {
  params?: Partial<AdminContractorGetParams>;
  paramsTransform?: Array<(params: Record<string, any>) => Record<string, any>>;
  options?: Omit<
    UseQueryOptions<unknown, Error, APIList<Contractor>>,
    'queryKey' | 'queryFn'
  >;
}

interface AdminContractorGetParams {
  limit: number;
  offset: number;
  sort: string;
  search: string;
  isActiveTrial: boolean;
  isActivePaidPlan: boolean;
  isRoot: boolean;
  heatIndexStandardId: number;
  assigneeId: string;
}

const schema: ObjectType['schema'] = {
  limit: 'number',
  offset: 'number',
  sort: 'string',
  search: 'string',
  isActiveTrial: 'boolean',
  isActivePaidPlan: 'boolean',
  isRoot: 'boolean',
  heatIndexStandardId: 'number',
  assigneeId: 'string',
}

export const useContractorListRequest = ({
  params = {},
  paramsTransform = [],
  options,
}: RequestParams = {}) => {
  const api = useApiInstance()

  const transformedParams = [
    transformPerPage,
    transformPage,
    ...paramsTransform,
  ].reduce((param, transform) => transform(param), params)

  const queryParams = buildRequestParams<AdminContractorGetParams>(
    transformedParams,
    schema,
  )

  return useQuery<unknown, Error, APIList<Contractor>>(
    [Entities.Contractors, JSON.stringify(transformedParams)],
    async () => {
      const { limit, offset, sort, isActivePaidPlan, isActiveTrial, search, isRoot, heatIndexStandardId, assigneeId } = queryParams
      const { data } = await api.v1AdminContractorsGet(
        limit,
        offset,
        sort,
        undefined,
        search,
        isActivePaidPlan,
        isActiveTrial,
        isRoot,
        heatIndexStandardId,
        assigneeId,
      )
      return data
    },
    {
      enabled: Object.keys(transformedParams).length > 0,
      ...options,
    },
  )
}
